import { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { PRISMAX_DATA_PIPELINE_URL } from '../../../configs/config';
import styles from './FirefoxVideoSupportNotification.module.css';

const STORAGE_KEY = 'firefoxVideoNotificationDisplayCount';
const SETTINGS_KEY = 'dont_show_again_firefox_video_notification';

function isFirefoxBrowser() {
    const ua = navigator.userAgent;
    return ua.includes('Firefox') || ua.includes('FxiOS');
}

export default function useFirefoxVideoSupportNotification({ enableFirefoxNotificationCheck = false } = {}) {
    const { userSettings, setUserSettings, gatewayToken } = useContext(SwapMainAppContext);
    const token = gatewayToken || localStorage.getItem('gatewayToken');

    const [shouldRender, setShouldRender] = useState(false);
    const hasIncremented = useRef(false);

    const handleDismiss = () => {
        setShouldRender(false);
    };

    const handleDontShowAgain = async () => {
        handleDismiss();
        setUserSettings(prev => ({
            ...prev,
            [SETTINGS_KEY]: true
        }));
        try {
            await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/user-settings/dont-show-again-firefox-video-notification`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
        } catch (err) {
            console.error('Failed to save Firefox notification preference:', err);
        }
    };

    useEffect(() => {
        if (!enableFirefoxNotificationCheck) return;
        if (!isFirefoxBrowser()) return;
        if (userSettings?.[SETTINGS_KEY]) return;
        if (shouldRender) return;

        const count = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
        if (count >= 2 && !hasIncremented.current) return; // Add the hasIncremented.current check so the code works in strict mode with double execution of the useEffect()

        if (!hasIncremented.current) {
            hasIncremented.current = true;
            localStorage.setItem(STORAGE_KEY, String(count + 1));
            setShouldRender(true);
        }

    }, [enableFirefoxNotificationCheck]);

    if (!shouldRender) return { node: null };

    const notification = (
        <div className={styles.notification}>
            <div className={styles.notificationTop}>
                <div className={styles.notificationIcon}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M6 2v4M6 8.5v.5" stroke="#e8a040" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </div>
                <div className={styles.notificationBody}>
                    <span className={styles.notificationTitle}>Firefox Video Support Tip</span>
                    <span className={styles.notificationDesc}>
                        Videos can sometimes be slow to load on Firefox. If that happens, please refresh the page or update your Firefox settings:
                    </span>
                    <ol className={styles.notificationSteps}>
                        <li>Navigate to <code className={styles.code}>about:config</code></li>
                        <li>Set <code className={styles.code}>network.http.http3.enable</code> to <code className={styles.code}>false</code></li>
                        <li>Restart your browser</li>
                    </ol>
                </div>
                <button className={styles.notificationClose} onClick={handleDismiss} aria-label="Dismiss">
                    ×
                </button>
            </div>
            <div className={styles.notificationFooter}>
                <button className={styles.notificationDismiss} onClick={handleDismiss}>
                    Dismiss
                </button>
                <button className={styles.dontShowAgain} onClick={handleDontShowAgain}>
                    Don't show again
                </button>
            </div>
        </div>
    );

    return { node: ReactDOM.createPortal(notification, document.body) };
}